import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { postEventRegistration } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Calendar, ArrowRight, Crown, Sparkles, Mic2, Loader2, CheckCircle2, Utensils, Presentation } from "lucide-react";

const TYPE_ICONS = {
  webinar: Mic2,
  masterclass: Presentation,
  conference: Sparkles,
  diner_business: Utensils,
};

const TIER_STYLES = {
  gratuit: "bg-white/5 border-white/10 text-zinc-300",
  premium: "bg-bicenter-cyan/10 border-bicenter-cyan/30 text-bicenter-cyan",
  vip: "bg-gradient-to-r from-bicenter-violet/20 to-bicenter-magenta/20 border-bicenter-magenta/40 text-bicenter-magenta",
};

export const Events = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const onRegister = (evt) => {
    setSelected(evt);
    setOpen(true);
  };

  return (
    <section id="events" className="relative py-28 md:py-36" data-testid="events-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/2 right-1/4 w-[500px] h-[500px] rounded-full bg-bicenter-magenta/10 blur-[180px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.4em] text-bicenter-magenta font-mono">{t.events.kicker}</p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]">
              {t.events.title}
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-8">
            <p className="text-zinc-400 text-lg font-body leading-relaxed">{t.events.subtitle}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.events.list.map((evt) => {
            const Icon = TYPE_ICONS[evt.type] || Mic2;
            return (
              <div
                key={evt.id}
                data-testid={`event-card-${evt.id}`}
                className="group relative bg-[#0c0c0e] border border-white/10 rounded-3xl p-6 hover:border-white/25 hover:-translate-y-1 transition-all duration-500 flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-xl border border-white/10 bg-white/[0.03] flex items-center justify-center">
                    <Icon size={18} className="text-white" />
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-full border ${TIER_STYLES[evt.tier]}`}>
                    {evt.tier === "vip" && <Crown size={10} className="inline mr-1 -mt-0.5" />}
                    {t.events.tiers[evt.tier]}
                  </span>
                </div>

                <p className="mt-5 text-[11px] uppercase tracking-[0.3em] text-zinc-500">
                  {t.events.types[evt.type]}
                </p>
                <h3 className="mt-2 font-display text-xl text-white font-medium leading-tight">
                  {evt.title}
                </h3>

                <div className="flex items-center gap-2 mt-3 text-sm text-bicenter-cyan">
                  <Calendar size={14} />
                  <span>{evt.date}</span>
                </div>

                <p className="mt-4 text-sm text-zinc-400 flex-1 leading-relaxed">{evt.description}</p>

                <button
                  onClick={() => onRegister(evt)}
                  data-testid={`event-register-${evt.id}`}
                  className="mt-6 inline-flex items-center justify-between gap-2 text-sm text-white font-medium border border-white/10 hover:border-bicenter-cyan/40 hover:bg-white/[0.03] rounded-full py-3 px-4 transition-all w-full group/btn"
                >
                  {t.events.register}
                  <ArrowRight size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <EventRegistrationDialog
        open={open}
        setOpen={setOpen}
        event={selected}
      />
    </section>
  );
};

const EventRegistrationDialog = ({ open, setOpen, event }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", company: "", role: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setForm({ name: "", email: "", company: "", role: "", phone: "", message: "" });
    setDone(false);
  };

  const close = () => {
    setOpen(false);
    setTimeout(reset, 400);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!event) return;
    setLoading(true);
    try {
      await postEventRegistration({
        ...form,
        event_id: event.id,
        event_title: event.title,
        event_type: event.type,
        tier: event.tier,
      });
      setDone(true);
      toast.success(t.events.form.success);
    } catch (err) {
      toast.error(t.events.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <DialogContent className="bg-[#0c0c0e] border-white/10 text-white max-w-lg" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white">
            {t.events.form.title}
          </DialogTitle>
          {event && (
            <DialogDescription className="text-sm text-zinc-400 mt-2">
              {event.title} · <span className="text-bicenter-cyan">{event.date}</span>
            </DialogDescription>
          )}
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center text-center py-8">
            <div className="w-14 h-14 rounded-full bg-bicenter-cyan/10 border border-bicenter-cyan/30 flex items-center justify-center mb-4">
              <CheckCircle2 size={24} className="text-bicenter-cyan" />
            </div>
            <p className="text-zinc-300">{t.events.form.success}</p>
            <Button onClick={close} className="mt-6 bg-white text-black hover:bg-zinc-200">OK</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <FormField label={t.events.form.name} required>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="event-input-name" className="bg-white/[0.03] border-white/10 text-white h-11" />
              </FormField>
              <FormField label={t.events.form.email} required>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="event-input-email" className="bg-white/[0.03] border-white/10 text-white h-11" />
              </FormField>
              <FormField label={t.events.form.company}>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="event-input-company" className="bg-white/[0.03] border-white/10 text-white h-11" />
              </FormField>
              <FormField label={t.events.form.role}>
                <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="event-input-role" className="bg-white/[0.03] border-white/10 text-white h-11" />
              </FormField>
            </div>
            <FormField label={t.events.form.phone}>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="event-input-phone" className="bg-white/[0.03] border-white/10 text-white h-11" />
            </FormField>
            <FormField label={t.events.form.message}>
              <Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="event-input-message" className="bg-white/[0.03] border-white/10 text-white" />
            </FormField>
            <Button
              type="submit"
              disabled={loading}
              data-testid="event-submit-button"
              className="w-full h-12 bg-gradient-to-r from-bicenter-cyan to-bicenter-blue text-white font-medium hover:shadow-[0_0_30px_rgba(0,169,255,0.4)]"
            >
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> {t.common.loading}
                </span>
              ) : t.events.form.submit}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const FormField = ({ label, required, children }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">
      {label} {required && <span className="text-bicenter-magenta">*</span>}
    </Label>
    {children}
  </div>
);
